import { useState, useCallback, useEffect } from "react";
import { RoomServiceItem } from "../types/roomServiceItem";
import { RoomServiceMenu } from "../types/roomServiceMenu";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";

interface UseRoomServiceItemsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
}

// Define PaginatedResponse locally if not available from a shared types file
// This should match the structure expected by the hook and returned by API functions.
// interface PaginatedResponse<T> {
//   data: T[];
//   pagination: {
//     page: number;
//     limit: number;
//     total: number;
//     totalPages: number;
//   };
// }

// Local helper to map backend API item shape to RoomServiceItem
interface ApiRoomServiceItem {
  // unified optional props to cover various backend casings
  rsItemId?: string;
  rsItemid?: string;
  rsitemid?: string;
  menuId?: string;
  roomservicemenuid?: string;
  itemName?: string;
  itemname?: string;
  description: string | null;
  ingredients: string | null;
  category: string | null;
  photo: string | null;
  price?: {
    priceId: string;
    amount: string;
    currencyCode: string;
  };

  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

const transformApiItem = (api: ApiRoomServiceItem): RoomServiceItem => {
  let parsedAmount = 0;
  if (api.price && api.price.amount !== undefined) {
    parsedAmount = parseFloat(api.price.amount);
  } else if (
    (api as unknown as Record<string, unknown>).priceAmount !== undefined
  ) {
    const maybeNum = Number(
      (api as unknown as Record<string, unknown>).priceAmount
    );
    parsedAmount = isNaN(maybeNum) ? 0 : maybeNum;
  }

  const base = api as unknown as Record<string, unknown>;
  const id = (api.rsItemId || api.rsItemid || base.rsitemid || "") as string;
  const menuId = (api.menuId || api.roomservicemenuid || "") as string;
  const name = (api.itemName || base.itemname || "") as string;
  return {
    rsitemid: id,
    roomservicemenuid: menuId,
    hotelid: "",
    itemname: name,
    description: api.description || "",
    ingredients: api.ingredients || "",
    category: api.category || "Default",
    photo: api.photo || null,
    price: api.price?.priceId || "",
    priceAmount: isNaN(parsedAmount) ? 0 : parsedAmount,
    currencyCode:
      api.price?.currencyCode || (base.currencyCode as string) || "USD",
    isavailable:
      (base.isAvailable as boolean | undefined) !== undefined
        ? (base.isAvailable as boolean)
        : (base.isactive as boolean | undefined) ?? false,
    isSpecial: false,
    specialStartDate: null,
    specialEndDate: null,
    spicelevel: "",
    createdat: api.createdAt,
    updatedat: api.updatedAt,
    resolvedPrice: api.price
      ? {
          priceId: api.price.priceId,
          amount: isNaN(parsedAmount) ? 0 : parsedAmount,
          currencyCode: api.price.currencyCode,
        }
      : null,
  };
};

export const useRoomServiceItems = ({
  hotelId,
  initialPage = 1,
  initialLimit = 1000,
}: UseRoomServiceItemsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const [items, setItems] = useState<RoomServiceItem[]>([]);
  const [roomServiceMenus, setRoomServiceMenus] = useState<RoomServiceMenu[]>(
    []
  );
  const [selectedRoomServiceMenuId, setSelectedRoomServiceMenuId] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<RoomServiceItem | null>(
    null
  );
  const [isMenusLoaded, setIsMenusLoaded] = useState(false);

  const {
    pagination,
    setPage,
    setLimit,
    setTotal: setPaginationTotal,
  } = usePagination({
    initialPage,
    initialLimit,
  });

  const fetchRoomServiceMenus = useCallback(async () => {
    if (!hotelId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/room-service-menus`,
      });

      const fetchedMenusOutput: RoomServiceMenu[] = response.data.data || [];
      setRoomServiceMenus(fetchedMenusOutput);
      setIsMenusLoaded(true);

      if (fetchedMenusOutput.length > 0 && !selectedRoomServiceMenuId) {
        setSelectedRoomServiceMenuId(fetchedMenusOutput[0].roomservicemenuid);
      }
    } catch (err) {
      console.error(
        "useRoomServiceItems: Error fetching room service menus:",
        err
      );
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch room service menus")
      );
      setRoomServiceMenus([]);
      setIsMenusLoaded(false);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, callApi]);

  const fetchItems = useCallback(async () => {
    if (!hotelId || !selectedRoomServiceMenuId || !isMenusLoaded) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items`,
      });

      const fetchedItems: RoomServiceItem[] = (response.data.data || []).map(
        (raw: ApiRoomServiceItem) => transformApiItem(raw)
      );

      setItems(fetchedItems);
      // total count equals fetched array length, update pagination state for consistency
      setPaginationTotal(fetchedItems.length);
    } catch (err) {
      console.error("useRoomServiceItems: Error fetching items:", err);
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch room service items")
      );
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    hotelId,
    selectedRoomServiceMenuId,
    callApi,
    setPaginationTotal,
    isMenusLoaded,
  ]);

  useEffect(() => {
    fetchRoomServiceMenus();
  }, [fetchRoomServiceMenus]);

  useEffect(() => {
    if (selectedRoomServiceMenuId && isMenusLoaded) {
      fetchItems();
    }
  }, [
    selectedRoomServiceMenuId,
    fetchItems,
    isMenusLoaded,
    pagination.page,
    pagination.limit,
  ]);

  const createItem = useCallback(
    async (
      userInputItem: Omit<
        RoomServiceItem,
        | "rsitemid"
        | "hotelid"
        | "roomservicemenuid"
        | "createdat"
        | "updatedat"
        | "price"
        | "resolvedPrice"
      >
    ) => {
      if (!selectedRoomServiceMenuId)
        throw new Error("No room service menu selected");

      // --- create/find price before creating item ---
      const priceAmount = userInputItem.priceAmount ?? 0;
      const currency = userInputItem.currencyCode || "USD";

      const priceResp = await callApi({
        method: "POST",
        url: "/management/prices",
        data: {
          amount: priceAmount.toString(),
          currencyCode: currency,
          priceType: "Room Service Item",
          description: `Price for ${userInputItem.itemname}`,
        },
      });

      const priceId = priceResp.data?.priceId;
      if (!priceId) throw new Error("Failed to create price record");

      const payload = {
        ...userInputItem,
        priceid: priceId,
        isactive: userInputItem.isavailable ?? true,
      } as Record<string, unknown>;

      const resp = await callApi({
        method: "POST",
        url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items`,
        data: payload,
      });

      const newItem = transformApiItem(
        resp.data.data as unknown as ApiRoomServiceItem
      );

      setItems((prev) => [newItem, ...prev]);

      // adjust total count for pagination (items not yet in state so add 1 to previous total)
      setPaginationTotal(items.length + 1);
    },
    [
      selectedRoomServiceMenuId,
      callApi,
      fetchItems,
      hotelId,
      setPaginationTotal,
      items,
    ]
  );

  const updateItem = useCallback(
    async (
      itemId: string,
      updates: Partial<
        Omit<
          RoomServiceItem,
          "hotelid" | "roomservicemenuid" | "createdat" | "updatedat"
        >
      >
    ) => {
      if (!selectedRoomServiceMenuId)
        throw new Error("No room service menu selected");

      // Map availability & price updates
      const payload: Record<string, unknown> = { ...updates };

      if (updates.isavailable !== undefined) {
        payload.isactive = updates.isavailable;
        delete payload.isavailable;
      }

      if (
        updates.priceAmount !== undefined ||
        updates.currencyCode !== undefined
      ) {
        const newAmount =
          updates.priceAmount ??
          items.find((i) => i.rsitemid === itemId)?.priceAmount ??
          0;
        const newCurrency =
          updates.currencyCode ??
          items.find((i) => i.rsitemid === itemId)?.currencyCode ??
          "USD";

        // Check if a price with same amount & currency exists
        const priceSearchResp = await callApi({
          method: "GET",
          url: "/management/prices",
          params: { page: 1, limit: 100 },
        });
        interface PriceEntry {
          priceId: string;
          amount: string;
          currency: { code: string };
        }
        const existing = (priceSearchResp.data.data as PriceEntry[]).find(
          (p) =>
            p.amount === newAmount.toFixed(2) && p.currency.code === newCurrency
        );

        let newPriceId: string;
        if (existing) {
          newPriceId = existing.priceId;
        } else {
          const priceCreate = await callApi({
            method: "POST",
            url: "/management/prices",
            data: {
              amount: newAmount.toString(),
              currencyCode: newCurrency,
              priceType: "Room Service Item",
              description: `Price for updated ${updates.itemname ?? "item"}`,
            },
          });
          newPriceId = priceCreate.data?.priceId;
        }

        payload.priceid = newPriceId;
        delete payload.priceAmount;
        delete payload.currencyCode;
      }

      // optimistic update
      const optimistic = {
        ...items.find((i) => i.rsitemid === itemId)!,
        ...updates,
      } as RoomServiceItem;
      setItems((prev) =>
        prev.map((it) => (it.rsitemid === itemId ? optimistic : it))
      );

      const resp = await callApi({
        method: "PUT",
        url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items/${itemId}`,
        data: payload,
      });

      const mergedRaw = {
        ...(items.find((i) => i.rsitemid === itemId) as RoomServiceItem),
        ...updates,
        ...(resp.data.data as unknown as Record<string, unknown>),
      } as unknown as ApiRoomServiceItem;
      const updated = transformApiItem(mergedRaw);

      setItems((prev) =>
        prev.map((it) => (it.rsitemid === itemId ? updated : it))
      );
    },
    [selectedRoomServiceMenuId, callApi, fetchItems, hotelId, items]
  );

  const deleteItem = useCallback(
    async (itemId: string) => {
      if (!selectedRoomServiceMenuId)
        throw new Error("No room service menu selected");

      try {
        console.log("Deleting room service item:", {
          itemId,
          selectedRoomServiceMenuId,
          url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items/${itemId}`,
          itemIdType: typeof itemId,
          menuIdType: typeof selectedRoomServiceMenuId,
          itemIdLength: itemId?.length,
          menuIdLength: selectedRoomServiceMenuId?.length,
          itemFromList: items.find((i) => i.rsitemid === itemId),
          allItemIds: items.map((i) => i.rsitemid),
          availableMenus: roomServiceMenus.map((m) => m.roomservicemenuid),
          currentMenu: roomServiceMenus.find(
            (m) => m.roomservicemenuid === selectedRoomServiceMenuId
          ),
        });

        await callApi({
          method: "DELETE",
          url: `/management/hotel/room-service-menus/${selectedRoomServiceMenuId}/items/${itemId}`,
        });

        // Update the items list immediately instead of refetching
        setItems((prev) => prev.filter((item) => item.rsitemid !== itemId));
      } catch (error) {
        console.error("Error deleting room service item:", error);
        throw error;
      }
    },
    [selectedRoomServiceMenuId, callApi]
  );

  // Apply a local optimistic update to an item (e.g., for preview images)
  const applyLocalItemUpdate = useCallback(
    (itemId: string, partial: Partial<RoomServiceItem>) => {
      setItems((prev) =>
        prev.map((it) => (it.rsitemid === itemId ? { ...it, ...partial } : it))
      );
    },
    []
  );

  const updateItemPrice = useCallback(
    async (itemId: string, price: number, currencyCode: string) => {
      if (!selectedRoomServiceMenuId)
        throw new Error("No room service menu selected");

      // Update price table directly then refresh local item
      const target = items.find((i) => i.rsitemid === itemId);
      if (!target?.price) throw new Error("Item missing priceId");

      await callApi({
        method: "PUT",
        url: `/management/prices/${target.price}`,
        data: {
          amount: price.toString(),
          currencyCode,
        },
      });

      // reflect locally
      setItems((prev) =>
        prev.map((it) =>
          it.rsitemid === itemId
            ? { ...it, priceAmount: price, currencyCode }
            : it
        )
      );
    },
    [selectedRoomServiceMenuId, callApi, items]
  );

  return {
    items,
    roomServiceMenus,
    selectedRoomServiceMenuId,
    setSelectedRoomServiceMenuId,
    isLoading,
    error,
    pagination,
    setPage,
    setLimit,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    updateItemPrice,
    fetchItems,
    fetchRoomServiceMenus,
    applyLocalItemUpdate,
  };
};
